import type {
  ConteudoDaAnaliseResponse,
  FonteDoGuideResponse,
  ItemDaAnaliseResponse,
} from '@/api/generated/model';

/**
 * A PONTE PARA AS ANÁLISES ANTIGAS — defesa em profundidade.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  `analises_ia.conteudo` é `Json` sem validação no banco. Toda análise gerada
 *  antes de a IA passar a citar o Guide está gravada com `achados: string[]` e
 *  `padroes` como string solta. O servidor já normaliza na leitura — mas o tipo
 *  que o orval gera AFIRMA a forma nova, e TypeScript não protege contra o que
 *  vem do banco: um registro antigo que escapasse chegaria ao `.map()` como
 *  `{ texto: undefined }` e quebraria o painel em runtime.
 *
 *  ⚠️ Roda ANTES dos guards de tamanho, não dentro do `.map()`. Os guards atuais
 *  (`conteudo.achados.length > 0`) passam com registro antigo e o render quebra
 *  por dentro — que é o pior lugar para descobrir.
 *
 *  Este arquivo é o cinto além do suspensório, e ele existe porque quem lê essa
 *  análise não pode regerá-la sem gastar outra chamada paga.
 * ═══════════════════════════════════════════════════════════════════════════
 */

export interface AnaliseNormalizada {
  achados: ItemDaAnaliseResponse[];
  padroes: ItemDaAnaliseResponse[];
  hipoteses: ItemDaAnaliseResponse[];
  investigar: ItemDaAnaliseResponse[];
  limitacoes: ItemDaAnaliseResponse[];
  guideCobriu: boolean;
}

export function normalizarAnalise(
  conteudo: ConteudoDaAnaliseResponse | null | undefined,
): AnaliseNormalizada | null {
  if (!conteudo) return null;

  const bruto = conteudo as unknown as Record<string, unknown>;

  return {
    achados: lista(bruto.achados),
    padroes: lista(bruto.padroes),
    hipoteses: lista(bruto.hipoteses),
    investigar: lista(bruto.investigar),
    limitacoes: lista(bruto.limitacoes),
    // Análise antiga não conhecia o Guide — dizer que ele cobriu seria mentira.
    guideCobriu: bruto.guideCobriu === true,
  };
}

/** A análise tem alguma frase escrita pela IA? */
export function temTextoDeIa(analise: AnaliseNormalizada): boolean {
  return (
    analise.achados.length > 0 ||
    analise.padroes.length > 0 ||
    analise.hipoteses.length > 0 ||
    analise.investigar.length > 0 ||
    analise.limitacoes.length > 0
  );
}

function lista(valor: unknown): ItemDaAnaliseResponse[] {
  // `padroes` era uma string única antes de virar lista.
  if (typeof valor === 'string') {
    return valor.trim() ? [{ texto: valor, fontes: [], semRespaldo: false }] : [];
  }

  if (!Array.isArray(valor)) return [];

  return valor.flatMap((item): ItemDaAnaliseResponse[] => {
    if (typeof item === 'string') return [{ texto: item, fontes: [], semRespaldo: false }];

    if (typeof item === 'object' && item !== null) {
      const atual = item as Partial<ItemDaAnaliseResponse>;
      if (typeof atual.texto !== 'string') return [];

      return [
        {
          texto: atual.texto,
          fontes: Array.isArray(atual.fontes) ? (atual.fontes as FonteDoGuideResponse[]) : [],
          semRespaldo: atual.semRespaldo === true,
        },
      ];
    }

    return [];
  });
}
